import { Form, Formik, type FormikHelpers } from 'formik';
import { parsePhoneOrEmail } from 'gcs-common/helper/formHelper';
import { phoneOrEmailValidator } from 'gcs-common/helper/yupValidationHelper';
import {
  getCreateErpCustomerUserDefaultErrorMessage,
  getCreateErpCustomerUserErrorMessage,
  getSelectedErpCustomer,
} from 'gcs-common/slices/administration/erpCustomers/erpCustomersSelectors';
import {
  resetCreateErpCustomerUserDefaultErrorMessage,
  resetCreateErpCustomerUserErrorMessage,
} from 'gcs-common/slices/administration/erpCustomers/erpCustomersSlice';
import {
  createErpCustomerUser,
  fetchErpCustomerUsers,
} from 'gcs-common/slices/administration/erpCustomers/erpCustomersThunks';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { showFailureNotification } from '../../../../slices/notifications/ShowNotificationThunk';
import Button from '../../../Button/Button';
import FormikInput from '../../../FormikInput/FormikInput';
import styles from './styles.module.scss';

const UserCreateValidationSchema = Yup.object().shape({
  name: Yup.string().nullable().required('Erforderlich'),
  phoneOrEmail: phoneOrEmailValidator,
});

interface UserCreateFormValues {
  erpCustomerName: string | undefined;
  customerNumber: string | undefined;
  name: string | undefined;
  phoneOrEmail: string | undefined;
}

interface ErpCustomerUserCreateProps {
  erpCustomerId: string;
  onCancel: VoidFunction;
  onSuccess: VoidFunction;
}

const ErpCustomerUserCreate = (props: ErpCustomerUserCreateProps) => {
  const { erpCustomerId, onCancel, onSuccess } = props;
  const dispatch = useDispatch();
  const errorMessage = useSelector(getCreateErpCustomerUserErrorMessage);
  const defaultErrorMessage = useSelector(getCreateErpCustomerUserDefaultErrorMessage);
  const selectedErpCustomer = useSelector(getSelectedErpCustomer(erpCustomerId));

  useEffect(() => {
    if (defaultErrorMessage) {
      dispatch(showFailureNotification(defaultErrorMessage, dispatch(resetCreateErpCustomerUserDefaultErrorMessage())));
    }
  }, [defaultErrorMessage, dispatch]);

  useEffect(() => {
    dispatch(resetCreateErpCustomerUserErrorMessage());
    dispatch(resetCreateErpCustomerUserDefaultErrorMessage());
  }, [dispatch]);

  const onEmailorPhoneChanged = useCallback(() => {
    if (errorMessage) {
      dispatch(resetCreateErpCustomerUserErrorMessage());
    }
  }, [dispatch, errorMessage]);

  const submit = useCallback(
    async (values: UserCreateFormValues, { setSubmitting }: FormikHelpers<UserCreateFormValues>) => {
      // validation guarantees a value here
      const user = { ...values, phoneOrEmail: undefined, ...parsePhoneOrEmail(values.phoneOrEmail ?? '') };
      // @ts-expect-error redux
      const result = await dispatch(createErpCustomerUser({ user, erpCustomerId }));
      setSubmitting(false);
      // @ts-expect-error redux
      if (!result.error) {
        onSuccess();
        // @ts-expect-error redux
        await dispatch(fetchErpCustomerUsers({ erpCustomerId }));
      }
    },
    [dispatch, onSuccess, erpCustomerId],
  );

  const initialData: UserCreateFormValues = {
    erpCustomerName: selectedErpCustomer?.friendlyName,
    customerNumber: selectedErpCustomer?.customerNumber,
    name: undefined,
    phoneOrEmail: undefined,
  };

  return (
    <Formik<UserCreateFormValues>
      initialValues={initialData}
      validationSchema={UserCreateValidationSchema}
      onSubmit={submit}
    >
      {({ isSubmitting }) => (
        <Form className={styles.column}>
          <div className={styles.row}>
            <FormikInput label="Firmename" name="erpCustomerName" disabled />
            <FormikInput label="Kundennummer" name="customerNumber" disabled />
          </div>

          <div className={styles.row}>
            <FormikInput label="Name*" type="text" name="name" />
          </div>

          <div className={styles.row}>
            <FormikInput
              label="Mobilnummer oder email-adresse*"
              type="text"
              name="phoneOrEmail"
              onKeyUp={onEmailorPhoneChanged}
            />
          </div>

          {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}

          <div className={styles.buttonRow}>
            <Button className={styles.modalButtonSecondary} type="button" disabled={isSubmitting} onClick={onCancel}>
              Abbrechen
            </Button>
            <Button className={styles.modalButton} type="submit" disabled={isSubmitting}>
              Speichern
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default ErpCustomerUserCreate;
